import React, { useEffect, useState } from 'react';
import { API_URL } from '../config';
import { useAuth } from '../contexts/AuthContext';
import './HistoryTable.css';

interface ParlayLeg {
  ingest_market_id: string;
  outcome_label: string;
  snap_implied_prob: number | null;
  ends_at: string | null;
  status: string;
  market_question: string;
  market_icon: string | null;
  event_title: string;
  event_image: string | null;
  category: string | null;
  sub_category: string | null;
}

interface ParlayTicket {
  parlay_id: string;
  total_amount: number;
  total_odds: number;
  expected_payout: number;
  status: string;
  settlement_date: string;
  created_at: string;
  tx_hash?: string;
  settlement_tx_hash?: string;
  actual_cashout_amount?: number;
  legs: ParlayLeg[];
}

const HistoryTable: React.FC = () => {
  const { user } = useAuth();
  const [parlays, setParlays] = useState<ParlayTicket[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchParlays = async () => {
      if (!user?.issuer) {
        setError('User not authenticated');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await fetch(`${API_URL}/user/parlays/active?user_issuer=${user.issuer}`, {
          method: 'GET',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        setParlays(data.parlays || []);
        setError(null);
      } catch (err) {
        console.error('Error fetching parlays:', err);
        setError('Failed to load parlay history');
      } finally {
        setLoading(false);
      }
    };

    fetchParlays();
  }, [user?.issuer]);

  const formatDate = (dateString: string | null): string => {
    if (!dateString) return 'TBD';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric'
      });
    } catch {
      return 'TBD';
    }
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const getStatusClass = (status: string): string => {
    switch (status.toLowerCase()) {
      case 'won':
      case 'paid':
        return 'status-won';
      case 'lost':
        return 'status-lost';
      case 'cashed_out':
        return 'status-cashed-out';
      case 'placed':
      case 'confirmed':
        return 'status-active';
      default:
        return 'status-default';
    }
  };

  const getStatusLabel = (status: string): string => {
    switch (status.toLowerCase()) {
      case 'won':
        return 'Won';
      case 'paid':
        return 'Paid';
      case 'lost':
        return 'Lost';
      case 'cashed_out':
        return 'Cashed Out';
      case 'placed':
        return 'Placed';
      case 'confirmed':
        return 'Confirmed';
      default:
        return status;
    }
  };

  const getPayoutAmount = (parlay: ParlayTicket): number => {
    if (parlay.status === 'cashed_out' && parlay.actual_cashout_amount) {
      return parlay.actual_cashout_amount;
    }
    if (parlay.status === 'won' || parlay.status === 'paid') {
      return parlay.expected_payout;
    }
    if (parlay.status === 'lost') {
      return 0;
    }
    return parlay.expected_payout;
  };

  const getBlockExplorerUrl = (txHash: string | null | undefined): string | null => {
    if (!txHash) return null;
    return `https://sepolia.basescan.org/tx/${txHash}`;
  };

  const truncateId = (id: string): string => {
    return id.slice(0, 8);
  };

  if (loading) {
    return (
      <div className="history-table-container">
        <div className="history-loading">Loading parlay history...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="history-table-container">
        <div className="history-error">{error}</div>
      </div>
    );
  }

  if (parlays.length === 0) {
    return (
      <div className="history-table-container">
        <div className="history-empty">
          <p>No parlay history found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="history-table-container">
      {/* Desktop table view */}
      <div className="history-table-wrapper">
        <table className="history-table">
          <thead>
            <tr>
              <th>Ticket ID</th>
              <th>Status</th>
              <th>Created</th>
              <th>Settlement</th>
              <th>Bet Amount</th>
              <th>Payout</th>
              <th>TX Link</th>
              <th>Settlement TX</th>
            </tr>
          </thead>
          <tbody>
            {parlays.map((parlay) => (
              <tr key={parlay.parlay_id}>
                <td className="ticket-id">
                  {truncateId(parlay.parlay_id)}
                </td>
                <td>
                  <span className={`status-badge ${getStatusClass(parlay.status)}`}>
                    {getStatusLabel(parlay.status)}
                  </span>
                </td>
                <td>{formatDate(parlay.created_at)}</td>
                <td>{formatDate(parlay.settlement_date)}</td>
                <td className="amount">{formatCurrency(parlay.total_amount)}</td>
                <td className="amount">{formatCurrency(getPayoutAmount(parlay))}</td>
                <td>
                  {getBlockExplorerUrl(parlay.tx_hash) ? (
                    <a
                      href={getBlockExplorerUrl(parlay.tx_hash)!}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="tx-link"
                    >
                      View
                    </a>
                  ) : (
                    <span className="no-data">TBD</span>
                  )}
                </td>
                <td>
                  {getBlockExplorerUrl(parlay.settlement_tx_hash) ? (
                    <a
                      href={getBlockExplorerUrl(parlay.settlement_tx_hash)!}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="tx-link"
                    >
                      View
                    </a>
                  ) : (
                    <span className="no-data">TBD</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile card view */}
      <div className="mobile-cards-container">
        {parlays.map((parlay) => (
          <div key={parlay.parlay_id} className="parlay-card">
            <div className="parlay-card-header">
              <span className={`status-badge ${getStatusClass(parlay.status)}`}>
                {getStatusLabel(parlay.status)}
              </span>
              <span className="parlay-card-ticket-id">
                {truncateId(parlay.parlay_id)}
              </span>
            </div>
            
            <div className="parlay-card-dates">
              <div className="parlay-card-date">
                <span className="parlay-card-date-label">Created:</span>
                <span>{formatDate(parlay.created_at)}</span>
              </div>
              <div className="parlay-card-date">
                <span className="parlay-card-date-label">Settlement:</span>
                <span>{formatDate(parlay.settlement_date)}</span>
              </div>
            </div>

            <div className="parlay-card-amounts">
              <div className="parlay-card-amount">
                <div className="parlay-card-amount-label">Bet Amount</div>
                <div className="parlay-card-amount-value">{formatCurrency(parlay.total_amount)}</div>
              </div>
              <div className="parlay-card-amount">
                <div className="parlay-card-amount-label">Payout</div>
                <div className="parlay-card-amount-value">{formatCurrency(getPayoutAmount(parlay))}</div>
              </div>
            </div>

            <div className="parlay-card-actions">
              {getBlockExplorerUrl(parlay.tx_hash) ? (
                <a
                  href={getBlockExplorerUrl(parlay.tx_hash)!}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="parlay-card-action"
                >
                  View TX
                </a>
              ) : (
                <span className="parlay-card-action disabled">View TX</span>
              )}
              {getBlockExplorerUrl(parlay.settlement_tx_hash) ? (
                <a
                  href={getBlockExplorerUrl(parlay.settlement_tx_hash)!}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="parlay-card-action"
                >
                  Settlement TX
                </a>
              ) : (
                <span className="parlay-card-action disabled">Settlement TX</span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default HistoryTable;
